import React, { useEffect, useMemo, useState } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import RenterLayout from '../components/layout/RenterLayout';
import { renterService } from '../services/renter.service';

const FILTERS = ['ALL', 'BOOKING', 'PAYMENT', 'REFUND', 'DRIVER', 'OWNER', 'ACCOUNT'];

const getClasses = (type) => {
  switch (type) {
    case 'PAYMENT':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'REFUND':
      return 'bg-amber-50 text-amber-700 border-amber-100';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

const RenterNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadNotifications = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await renterService.getNotifications();
        setNotifications(data || []);
      } catch (err) {
        setError(err.message || 'Failed to load notifications.');
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, []);

  const filteredNotifications = useMemo(() => {
    if (filter === 'ALL') return notifications;
    return notifications.filter((item) => item.type === filter);
  }, [filter, notifications]);

  return (
    <RenterLayout
      activePage="Notifications"
      account compact
      title="Notifications"
      description="Updates from your customer, driver, and car owner activity, together in one place."
    >
      {loading ? (
        <div className="min-h-[320px] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="rounded-[1.75rem] border border-red-100 bg-red-50 px-6 py-5 text-red-700 font-semibold">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-3">
            {FILTERS.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setFilter(item)}
                className={`rounded-full px-5 py-3 text-[10px] font-black uppercase tracking-[0.18em] transition-colors ${
                  filter === item
                    ? 'bg-brand-black text-white'
                    : 'bg-[#f8fafc] text-gray-500 border border-gray-200'
                }`}
              >
                {item === 'ALL' ? 'All Alerts' : item}
              </button>
            ))}
          </div>

          {filteredNotifications.length === 0 ? (
            <div className="rounded-[1.75rem] border border-gray-100 bg-[#f8fafc] px-8 py-14 text-center">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-black tracking-tight mb-2">No notifications in this filter</h2>
              <p className="text-gray-500 font-medium">Try another filter or come back after your next booking update.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredNotifications.map((notification, index) => (
                <div key={`${notification.title}-${index}`} className="rounded-[1.75rem] border border-gray-100 bg-white px-6 py-5">
                  <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                    <span className={`rounded-full border px-4 py-2 text-[10px] font-black uppercase tracking-[0.18em] ${getClasses(notification.type)}`}>
                      {notification.type}
                    </span>
                    <span className="text-[11px] font-bold text-gray-400">{notification.time}</span>
                  </div>
                  <h2 className="text-xl font-black tracking-tight mb-2">{notification.title}</h2>
                  <p className="text-sm text-gray-500 leading-7">{notification.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </RenterLayout>
  );
};

export default RenterNotifications;
